import { clamp, hashSeed, mulberry32, smoothstep } from './math';
import type { DensityFieldConfig, DerivedSeed, HotSpot, Site, SourcePoint } from './types';

export function deriveFromSeed(text: string): DerivedSeed | null {
  if (!text || !text.trim()) return null;
  const h = hashSeed(text.trim().toLowerCase());
  return {
    outcomes: 2 + (h % 4),
    skew: (((h >> 8) & 0xff) / 255) * 0.5 + 0.25,
    axis: (h >> 4) & 1 ? 'radial' : 'horizontal',
    phase: (((h >> 16) & 0xff) / 255) * Math.PI * 2,
    paletteRoll: (h >> 20) & 0xff,
    turbulence: 0.3 + (((h >> 24) & 0xff) / 255) * 0.7,
    hash: h,
  };
}

export type DensityConfigOverrides = Partial<
  Pick<
    DensityFieldConfig,
    | 'srcLayout'
    | 'srcCount'
    | 'srcSize'
    | 'srcElong'
    | 'srcFalloff'
    | 'srcDivide'
    | 'kaleN'
    | 'kaleRot'
  >
>;

export const SOURCE_PRESET_PARAMS: Readonly<
  Record<
    string,
    Pick<
      DensityFieldConfig,
      'srcLayout' | 'srcCount' | 'srcSize' | 'srcElong' | 'srcFalloff' | 'srcDivide'
    >
  >
> = {
  surge: {
    srcLayout: 'center',
    srcCount: 1,
    srcSize: 0.72,
    srcElong: 1.0,
    srcFalloff: 0.32,
    srcDivide: 0,
  },
  crossfade: {
    srcLayout: 'pair',
    srcCount: 2,
    srcSize: 0.6,
    srcElong: 1.0,
    srcFalloff: 0.4,
    srcDivide: 0,
  },
  heatmap: {
    srcLayout: 'scatter',
    srcCount: 6,
    srcSize: 0.45,
    srcElong: 2.2,
    srcFalloff: 0.55,
    srcDivide: 0,
  },
  polarization: {
    srcLayout: 'row',
    srcCount: 4,
    srcSize: 0.4,
    srcElong: 1.3,
    srcFalloff: 0.5,
    srcDivide: 0.7,
  },
  defection: {
    srcLayout: 'corners',
    srcCount: 2,
    srcSize: 0.58,
    srcElong: 1.2,
    srcFalloff: 0.45,
    srcDivide: 0.4,
  },
  split: {
    srcLayout: 'pair',
    srcCount: 2,
    srcSize: 0.7,
    srcElong: 1.4,
    srcFalloff: 0.45,
    srcDivide: 0.8,
  },
};

export function buildDensityConfig(
  mode: string,
  seed: number | string,
  width: number,
  height: number,
  overrides?: DensityConfigOverrides,
): DensityFieldConfig {
  const seedStr = String(seed);
  const seedHash = hashSeed(`${seedStr}-${mode}`);
  const preset = SOURCE_PRESET_PARAMS[mode];
  const defaultKaleN = mode === 'breakthrough' ? 8 : 1;

  const base: DensityFieldConfig = {
    width,
    height,
    mode,
    seed: seedHash,
    derived: deriveFromSeed(seedStr),
    srcLayout: preset?.srcLayout ?? 'scatter',
    srcCount: preset?.srcCount ?? 6,
    srcSize: preset?.srcSize ?? 0.45,
    srcElong: preset?.srcElong ?? 2.2,
    srcFalloff: preset?.srcFalloff ?? 0.55,
    srcDivide: preset?.srcDivide ?? 0,
    convN: 4,
    convGap: 0.18,
    convRot: 0,
    convTwist: 0,
    kaleN: defaultKaleN,
    kaleRot: 0,
    matrixCell: 40,
    matrixFill: 0.7,
    matrixLevels: 1,
    matrixBias: 'scatter',
    _cache: {},
  };

  if (overrides === undefined) {
    return base;
  }

  return {
    ...base,
    ...overrides,
    derived: base.derived,
    _cache: {},
  };
}

function hotSet(o: DensityFieldConfig, n: number): HotSpot[] {
  const key = `_hot${n}`;
  const cached = o._cache[key];
  if (cached !== undefined) return cached as HotSpot[];

  const r = mulberry32(((o.seed || 0) ^ 0x51ed2701) >>> 0);
  const s: HotSpot[] = [];
  const minS = Math.min(o.width, o.height);
  for (let i = 0; i < n; i++) {
    const elong = 0.55 + r() * 1.05;
    const base = minS * (0.1 + r() * 0.15);
    s.push({
      x: (0.12 + r() * 0.76) * o.width,
      y: (0.14 + r() * 0.72) * o.height,
      rx: base * elong,
      ry: base / Math.sqrt(elong),
      rot: r() * Math.PI,
      amp: 0.6 + r() * 0.5,
    });
  }
  o._cache[key] = s;
  return s;
}

function siteSet(o: DensityFieldConfig, n: number): Site[] {
  const key = `_sites${n}`;
  const cached = o._cache[key];
  if (cached !== undefined) return cached as Site[];

  const r = mulberry32(((o.seed || 0) ^ 0x9e3779b1) >>> 0);
  const s: Site[] = [];
  for (let i = 0; i < n; i++) {
    s.push({
      x: (0.08 + r() * 0.84) * o.width,
      y: (0.08 + r() * 0.84) * o.height,
      w: 0.55 + r() * 0.9,
    });
  }
  o._cache[key] = s;
  return s;
}

function sourceList(o: DensityFieldConfig): SourcePoint[] {
  const key = `_src${o.srcLayout}${o.srcCount}${o.seed || 0}`;
  const cached = o._cache[key];
  if (cached !== undefined) return cached as SourcePoint[];

  const N = Math.max(1, o.srcCount | 0 || 1);
  const W = o.width;
  const H = o.height;
  const cx = W * 0.5;
  const cy = H * 0.52;
  const minS = Math.min(W, H);
  const list: SourcePoint[] = [];
  const r = mulberry32(((o.seed || 0) ^ 0x5bd1e995) >>> 0);
  const layout = o.srcLayout || 'scatter';
  const cols = Math.max(1, Math.round(Math.sqrt(N)));
  const rows = Math.ceil(N / cols);

  for (let i = 0; i < N; i++) {
    let px: number;
    let py: number;
    let rot = 0;
    if (layout === 'center') {
      px = cx;
      py = cy;
    } else if (layout === 'pair') {
      px = W * (i % 2 === 0 ? 0.32 : 0.68);
      py = cy + (i > 1 ? (i - 1.5) * 0.12 * H : 0);
    } else if (layout === 'row') {
      px = W * (0.5 + (N > 1 ? (i / (N - 1) - 0.5) * 0.74 : 0));
      py = cy + Math.sin(i * 1.7) * H * 0.04;
    } else if (layout === 'corners') {
      const corners: ReadonlyArray<readonly [number, number]> = [
        [0.22, 0.26],
        [0.78, 0.74],
        [0.78, 0.26],
        [0.22, 0.74],
      ];
      const pxPct = corners[i % 4]?.[0] ?? 0.22;
      const pyPct = corners[i % 4]?.[1] ?? 0.26;
      px = W * pxPct;
      py = H * pyPct;
    } else if (layout === 'ring') {
      const a = (i / N) * Math.PI * 2;
      px = cx + Math.cos(a) * minS * 0.3;
      py = cy + Math.sin(a) * minS * 0.3;
    } else {
      const ci = Math.floor((i * cols * rows) / N);
      const gx = ci % cols;
      const gy = Math.floor(ci / cols);
      const cw = W / cols;
      const ch = H / rows;
      px = (gx + 0.15 + r() * 0.7) * cw;
      py = (gy + 0.15 + r() * 0.7) * ch;
      rot = r() * Math.PI;
    }
    list.push({ x: px, y: py, rot });
  }
  o._cache[key] = list;
  return list;
}

function sourcesField(x: number, y: number, o: DensityFieldConfig): number {
  const W = o.width;
  const minS = Math.min(W, o.height);
  const cx = W * 0.5;
  const base = minS * (0.1 + (o.srcSize == null ? 0.45 : o.srcSize) * 0.3);
  const elong = Math.max(1, o.srcElong || 1);
  const rx = base * Math.sqrt(elong);
  const ry = base / Math.sqrt(elong);
  const sharp = 0.8 + (o.srcFalloff == null ? 0.5 : o.srcFalloff) * 3.4;
  const wave = 0.5 + 0.5 * Math.sin(x * 0.009 + y * 0.006 + (o.seed || 0) * 0.001);
  const list = sourceList(o);
  let acc = 0;
  for (const s of list) {
    const dx = x - s.x;
    const dy = y - s.y;
    const c = Math.cos(-s.rot);
    const si = Math.sin(-s.rot);
    const lx = (dx * c - dy * si) / rx;
    const ly = (dx * si + dy * c) / ry;
    acc += Math.exp(-(lx * lx + ly * ly) * sharp);
  }
  let v = Math.min(1.25, acc);
  if (o.srcDivide) {
    const g = 1 - smoothstep(0, W * 0.16 * o.srcDivide + 1, Math.abs(x - cx));
    v *= 1 - g * o.srcDivide;
  }
  return clamp(0.05 + v * 0.85 + wave * 0.04, 0, 1);
}

export { hotSet, siteSet, sourceList, sourcesField };
